// src/oura/components/newscan/OutcomeLine.tsx
//
// The one line under the decisions that says what the last fix did, with
// Undo beside it: "3 fixed. Last: Price set to RM120 (your choice) · Undo".
// It is not a live region: the foot's message line already says each outcome
// once, and saying it twice would read it twice.

import { CheckIcon } from "lucide-react";
import { cn } from "@oura/lib/cn";
import { PillButton, TYPE } from "@oura/ui";
import { OUTCOME } from "@oura/newscan/copy";

export interface OutcomeLineProps {
    /** The last change's label (`draft.undo.lastLabel` or `ctl.outcome.label`). Nothing renders without one. */
    last: string | null;
    /** Decisions answered so far (not rewords, locks or word edits). Above 0 it leads: "3 fixed. Last: …". Absent: no count. */
    count?: number;
    /** Undo the last change. Omit to show no Undo. */
    onUndo?: () => void;
    /** Undo off while another write runs. */
    undoDisabled?: boolean;
    /** Undo is the write running now: it spins. */
    undoBusy?: boolean;
    className?: string;
}

export function OutcomeLine({ last, count = 0, onUndo, undoDisabled, undoBusy, className }: OutcomeLineProps) {
    if (!last) return null;
    return (
        <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-gray-700", className)}>
            <CheckIcon aria-hidden size={14} className="shrink-0 text-emerald-600" />
            {count > 0 && <span className="font-semibold tabular-nums text-gray-900">{OUTCOME.fixed(count)}</span>}
            {count > 0 && <span className="text-gray-500">{OUTCOME.last}</span>}
            <span className="min-w-0 break-words">{last}</span>
            {onUndo && (
                <>
                    <span aria-hidden className="text-gray-300">
                        ·
                    </span>
                    <PillButton
                        variant="text"
                        busy={undoBusy}
                        disabled={undoDisabled}
                        onClick={onUndo}
                        aria-label={OUTCOME.undoLabel(last)}
                    >
                        {OUTCOME.undo}
                    </PillButton>
                </>
            )}
        </p>
    );
}
